import React from 'react';
import { Search, X } from 'lucide-react';
import {
  Input,
  Spinner,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@hero/web-components';
import { CurrentLocationIcon } from '@hero/web-icons';

export interface MapSearchInputProps {
  value: string;
  placeholder: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onClear: () => void;
  onGetCurrentLocation: () => void;
  isLoading: boolean;
  isLoadingCoordinates: boolean;
  isGettingCurrentLocation: boolean;
  isRTL: boolean;
  showClear: boolean;
  searchInputRef: React.RefObject<HTMLInputElement>;
  t: (key: string, defaultValue?: string) => string;
}

export function MapSearchInput({
  value,
  placeholder,
  onChange,
  onFocus,
  onBlur,
  onKeyDown,
  onClear,
  onGetCurrentLocation,
  isLoading,
  isLoadingCoordinates,
  isGettingCurrentLocation,
  isRTL,
  showClear,
  searchInputRef,
  t,
}: MapSearchInputProps) {
  return (
    <div className="relative">
      {isLoading || isLoadingCoordinates ? (
        <div
          className={`absolute transform -translate-y-1/2 ${isRTL ? 'right-3' : 'left-3'} top-1/2`}>
          <Spinner />
        </div>
      ) : (
        <Search
          className={`absolute transform -translate-y-1/2 ${
            isRTL ? 'right-3' : 'left-3'
          } top-1/2 w-4 h-4 text-gray-400`}
        />
      )}

      <Input
        ref={searchInputRef}
        type="text"
        className={`${isRTL ? 'pr-20 pl-10 text-right' : 'pl-10 pr-20'}`}
        value={value}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        placeholder={placeholder || t('location.searchPlaceholder', 'Search for a location...')}
      />

      {/* Current Location Button - Positioned before clear button */}
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              type="button"
              onClick={onGetCurrentLocation}
              disabled={isGettingCurrentLocation}
              className={`absolute p-1.5 transform -translate-y-1/2 ${
                isRTL ? 'left-10' : 'right-10'
              } top-1/2 hover:bg-gray-100`}>
              {isGettingCurrentLocation ? (
                <Spinner className="w-4 h-4 text-[#4285F4]" />
              ) : (
                <CurrentLocationIcon strokeWidth={2} variant="primary" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side={isRTL ? 'left' : 'right'}>
            <p>{t('location.myLocation')}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      {/* Clear Button (X) - Show when there's text or placeholder, positioned at edge */}
      {showClear && (
        <Button
          variant="ghost"
          size="sm"
          className={`absolute p-1 transform -translate-y-1/2 ${
            isRTL ? 'left-2' : 'right-2'
          } top-1/2 hover:bg-gray-100`}
          onClick={onClear}>
          <X className="w-4 h-4" />
        </Button>
      )}
    </div>
  );
}
